import { useRef, useState } from 'react'

import { usePlayerAuth } from '../../contexts/PlayerAuthContext'
import { usePasskeys } from '../../hooks/usePasskeys'
import type { PasskeySummary } from '../../services/account/authApi'
import { getAuthErrorMessage } from '../../services/account/authErrors'
import { describeThisDevice } from '../../utils/deviceLabel'
import { shouldOfferPasskey } from '../../utils/passkeyOffer'
import { getPasskeyOfferDismissedAt, setPasskeyOfferDismissedAt } from '../../utils/storage'
import { errorClass, linkButtonClass, primaryButtonClass, secondaryButtonClass } from './styles'

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

// Passkeys for the signed-in player: an offer to add the first one, then a list
// with add and remove. Renders nothing unless the server has passkeys on and this
// device can use them. Nothing here is required: the email code always works.
export function PasskeyManager() {
  const { passkeyAvailable } = usePlayerAuth()
  const { passkeys, status, add, remove, reload } = usePasskeys(passkeyAvailable)

  const [adding, setAdding] = useState(false)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Read once when the page opens; the clock is not read during render.
  const [openedAt] = useState(() => Date.now())
  const [lastDismissedAt] = useState(() => getPasskeyOfferDismissedAt())
  const [dismissedNow, setDismissedNow] = useState(false)
  // Refs are the real lock: state is stale within one tick, so a double click could
  // otherwise start two prompts or two removals.
  const inFlight = useRef(false)

  if (!passkeyAvailable) return null

  const loaded = status === 'ready' && passkeys !== null
  const offer =
    !dismissedNow &&
    shouldOfferPasskey({
      enabledByServer: true,
      supportedByDevice: true,
      loaded,
      hasPasskeys: (passkeys?.length ?? 0) > 0,
      lastDismissedAt,
      now: openedAt,
    })

  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setError(null)
    setNotice(null)
    try {
      await action()
    } catch (err) {
      setError(getAuthErrorMessage(err))
    } finally {
      inFlight.current = false
    }
  }

  const addPasskey = () =>
    run(async () => {
      setAdding(true)
      try {
        if (await add(describeThisDevice())) setNotice('Passkey added. Next time you can sign in with it.')
      } finally {
        setAdding(false)
      }
    })

  const removePasskey = (id: string) =>
    run(async () => {
      setRemovingId(id)
      try {
        await remove(id)
        setConfirmingId(null)
        setNotice('Passkey removed.')
      } finally {
        setRemovingId(null)
      }
    })

  const dismissOffer = () => {
    setDismissedNow(true)
    setPasskeyOfferDismissedAt(Date.now())
  }

  const messages = (
    <>
      {error && (
        <p role="alert" data-testid="passkey-error" className={errorClass}>
          {error}
        </p>
      )}
      {notice && (
        <p role="status" data-testid="passkey-notice" className="text-sm text-green-400">
          {notice}
        </p>
      )}
    </>
  )

  if (status === 'error') {
    return (
      <section aria-labelledby="passkeys-heading" className="space-y-2">
        <h3 id="passkeys-heading" className="text-lg font-semibold">
          Passkeys
        </h3>
        <p role="alert" data-testid="passkeys-load-error" className={errorClass}>
          Could not load your passkeys. Check your connection.
        </p>
        <button type="button" onClick={reload} className={linkButtonClass}>
          Try again
        </button>
      </section>
    )
  }

  if (!loaded) {
    return (
      <p role="status" data-testid="passkeys-loading" className="text-sm text-white/50">
        Loading passkeys...
      </p>
    )
  }

  if (offer) {
    return (
      <section
        aria-labelledby="passkey-offer-heading"
        data-testid="passkey-offer"
        className="space-y-3 rounded-2xl border border-purple-500/35 bg-purple-500/10 p-4">
        <div>
          <h3 id="passkey-offer-heading" className="font-bold text-white">
            Skip the email code next time
          </h3>
          <p className="mt-1 text-sm text-white/75">
            Sign in with your fingerprint, face or screen lock. Nothing to type, and it cannot be phished. You can still
            use an email code any time.
          </p>
        </div>
        {messages}
        <div className="flex items-center gap-4">
          <button type="button" disabled={adding} onClick={() => void addPasskey()} className={primaryButtonClass}>
            {adding ? 'Waiting for your device...' : 'Add a passkey'}
          </button>
          <button type="button" disabled={adding} onClick={dismissOffer} className={linkButtonClass}>
            Not now
          </button>
        </div>
      </section>
    )
  }

  return (
    <section aria-labelledby="passkeys-heading" className="space-y-3">
      <div>
        <h3 id="passkeys-heading" className="text-lg font-semibold">
          Passkeys
        </h3>
        <p className="text-sm text-white/60">Sign in with your fingerprint, face or screen lock instead of a code.</p>
      </div>

      {(passkeys ?? []).length > 0 && (
        <ul className="space-y-2">
          {(passkeys ?? []).map((passkey) => (
            <PasskeyRow
              key={passkey.id}
              passkey={passkey}
              confirming={confirmingId === passkey.id}
              removing={removingId === passkey.id}
              onAskRemove={() => setConfirmingId(passkey.id)}
              onCancel={() => setConfirmingId(null)}
              onRemove={() => void removePasskey(passkey.id)}
            />
          ))}
        </ul>
      )}

      {messages}

      <button type="button" disabled={adding} onClick={() => void addPasskey()} className={secondaryButtonClass}>
        {adding ? 'Waiting for your device...' : (passkeys ?? []).length > 0 ? 'Add another passkey' : 'Add a passkey'}
      </button>
    </section>
  )
}

function PasskeyRow({
  passkey,
  confirming,
  removing,
  onAskRemove,
  onCancel,
  onRemove,
}: {
  passkey: PasskeySummary
  confirming: boolean
  removing: boolean
  onAskRemove: () => void
  onCancel: () => void
  onRemove: () => void
}) {
  return (
    <li data-testid="passkey-row" className="rounded-lg border border-white/10 bg-[#222]/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-white/90">{passkey.nickname ?? 'Passkey'}</p>
          <p className="text-xs text-white/50">
            {passkey.synced ? 'Synced across your devices' : 'This device only'} · Added {formatDate(passkey.createdAt)} ·{' '}
            {passkey.lastUsedAt ? `Last used ${formatDate(passkey.lastUsedAt)}` : 'Never used'}
          </p>
        </div>
        {!confirming && (
          <button
            type="button"
            onClick={onAskRemove}
            aria-label={`Remove ${passkey.nickname ?? 'passkey'}`}
            className="shrink-0 text-sm text-red-400 underline hover:text-red-300">
            Remove
          </button>
        )}
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Remove this passkey?" className="mt-3 space-y-2 rounded-lg bg-red-400/5 p-3">
          <p className="text-sm text-white/80">
            You will no longer be able to sign in with it here. It is not deleted from your password manager or device;
            you can remove it there too.
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              disabled={removing}
              onClick={onRemove}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
              {removing ? 'Removing...' : 'Remove passkey'}
            </button>
            <button type="button" disabled={removing} onClick={onCancel} className={linkButtonClass}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  )
}
